import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef, Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { Action } from 'src/app/core-module/models/Compliance-Actions/action';
import { ActionHistory } from 'src/app/core-module/models/Audits/action-history';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { ActionSummaryCardComponent } from './action-summary-card/action-summary-card.component';
import { ActionContextCardComponent } from './action-context-card/action-context-card.component';
import { ActionUpdatesComponent, ActionUpdateVM } from './action-updates/action-updates.component';
import { ActionHistoryTimelineComponent } from './action-history-timeline/action-history-timeline.component';
import { ActionAssetsComponent, ActionAttachmentVM } from './action-assets/action-assets.component';
import { ActionChildrenComponent } from './action-children/action-children.component';
import { BasicAction } from 'src/app/core-module/models/Audits/AuditResults/basic-action';
import { ComplianceActionService } from 'src/app/core-module/services/compliance-action.service';
import { EventsService } from 'src/app/core-module/services/events.service';
import { ActionUserRequest } from 'src/app/core-module/models/Compliance-Actions/action-user-request';
import { EventOverview } from 'src/app/core-module/models/Events/event-overview';
import { GetEventResult } from 'src/app/core-module/models/Events/get-event-result';
import { BasicResult } from 'src/app/core-module/models/basic-result';
import { ActionResponse } from 'src/app/core-module/models/Compliance-Actions/action-response';
import { EventActionAddComponent } from '../event-page/event-action-add/event-action-add.component';
import { ComplianceAction } from 'src/app/core-module/models/Audits/compliance-action';
import { ActionPhoto } from 'src/app/core-module/models/Compliance-Actions/action-photo';
import { ActionFile } from 'src/app/core-module/models/Compliance-Actions/action-file';
import { QuestionAttachment } from 'src/app/core-module/models/Audits/question-attachment';
import { environment } from 'src/environments/environment';
import { SelectModule } from 'primeng/select';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';
import { CustomerTermPipe } from 'src/app/shared-module/pipes/customer-term.pipe';

@Component({
  selector: 'app-action-detail-v2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ActionSummaryCardComponent,
    ActionContextCardComponent,
    ActionUpdatesComponent,
    ActionHistoryTimelineComponent,
    ActionAssetsComponent,
    ActionChildrenComponent,
    EventActionAddComponent,
    SelectModule,
    ConfirmPopupModule,
    CustomerTermPipe
  ],
  providers: [ConfirmationService],
  templateUrl: './action-detail-v2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './action-detail-v2.component.scss'
})
export class ActionDetailV2Component {

  action: Action = new Action();
  users: BasicUser[] = [];
  history: ActionHistory[] = [];
  updates: ActionUpdateVM[] = [];
  attachments: ActionAttachmentVM[] = [];
  photos: string[] = [];
  childActions: BasicAction[] = [];
  loading: boolean = true;
  editingSummary: boolean = false;
  private summarySnapshot: {
    description: string;
    ownerId: number;
    ownerTypeId: number;
    assignee: string;
    deadline: number | null;
  } | null = null;
  saving: boolean = false;
  validationError: string = '';
  descriptionError: string = '';
  assigneeError: string = '';
  dateError: string = '';
  originalDeadline: Date | null = null;
  contextType: 'Event' | 'Audit' = 'Event';
  contextTitle: string = '';
  contextSubtitle: string = '';
  contextBody: string = '';
  contextMeta: string = '';
  updateError: string = '';
  updateSaving: boolean = false;
  childAdding: boolean = false;
  childSaving: boolean = false;
  childResetToken: number = 0;
  assetError: string = '';
  photoUploading: boolean = false;
  attachmentUploading: boolean = false;
  backLink: any = '/actions';
  backParams: any = {};
  backLabel: string = 'Back to Actions';
  backState: any = {};
  showAuditRequirementPanel: boolean = false;
  showAuditSelector: boolean = false;
  eligibleAudits: any[] = [];
  selectedAuditResultId: number | null = null;
  attachingAudit: boolean = false;
  actionAuditOptions: { AuditID: number; AuditName: string }[] = [];
  loadingActionAudits: boolean = false;

  constructor(private route: ActivatedRoute, private actionService: ComplianceActionService, private eventService: EventsService, private confirmationService: ConfirmationService, private cdr: ChangeDetectorRef, private userManagement: UserManagementService){}

  ngOnInit(){
    this.route.queryParams.subscribe(params => {
      const id = Number(params['id'] ?? -1);
      const from = params['from'];
      const eventId = Number(params['eventId'] ?? -1);
      const filters = params['filters'];
      const auditResultId = Number(params['auditResultId'] ?? -1);
      const questionId = Number(params['questionId'] ?? -1);
      const returnUrl = params['returnUrl'];
      const backQuestion = history.state?.question;
      if (backQuestion){
        this.backState = { question: backQuestion };
      }
      this.configureBackLink(from, eventId, filters, returnUrl, auditResultId, questionId);
      if (id && id !== -1){
        this.loadAction(id);
      } else {
        this.loading = false;
      }
    });
  }

  private configureBackLink(from?: string, eventId?: number, filters?: string, returnUrl?: string, auditResultId?: number, questionId?: number){
    if (from === 'event'){
      this.backLabel = 'Back to Event';
      this.backLink = eventId && eventId > 0 ? ['/events', eventId] : '/events';
      this.backParams = { tab: 'actions' };
      return;
    }
    if (from === 'home'){
      this.backLabel = 'Back to Home';
      this.backLink = '/home';
      this.backParams = {};
      return;
    }
    if (from === 'event-question-edit'){
      this.backLabel = 'Back to Question';
      if (returnUrl){
        const parsed = this.parseReturnUrl(returnUrl);
        this.backLink = parsed.path || '/event-question-edit';
        this.backParams = parsed.params;
        if (this.backState?.question){
          this.backState = { question: this.backState.question };
        }
        return;
      }
      this.backLink = ['/event-question-edit'];
      this.backParams = { eventId, auditResultId, questionId, tab: 'details' };
      return;
    }
    if (from === 'results'){
      this.backLabel = 'Back to Results';
      this.backLink = '/results';
      this.backParams = auditResultId && auditResultId > 0 ? { auditResultId } : {};
      return;
    }
    this.backLabel = 'Back to Actions';
    this.backLink = '/actions';
    this.backParams = filters ? { filters } : {};
  }

  private parseReturnUrl(url: string): { path: string; params: any }{
    if (!url){
      return { path: '/event-question-edit', params: {} };
    }
    const parts = url.split('?');
    const path = parts[0] || '/event-question-edit';
    const params: any = {};
    if (parts.length > 1){
      const search = new URLSearchParams(parts[1]);
      search.forEach((v,k) => { params[k] = v; });
    }
    return { path, params };
  }

  private loadAction(actionId:number){
    this.loading = true;
    this.actionService.getActionByID(actionId).subscribe({
      next: (a:Action) => {
        this.action = a;
        if (this.action?.ActionDeadline){
          this.action.ActionDeadline = new Date(this.action.ActionDeadline);
          this.originalDeadline = new Date(this.action.ActionDeadline);
        }
        if (this.action?.ResolvedDate){
          this.action.ResolvedDate = new Date(this.action.ResolvedDate);
        }
        this.hydrateUpdates();
        this.loading = false;
        this.loadHistory(actionId);
        this.loadUsers();
        if (this.action.EventID && this.action.EventID !== -1){
          this.loadEventContext(this.action.EventID); 
          this.loadChildActions(this.action.EventID, true);
          this.loadActionAudits(this.action.AuditLocation?.LocationID);
        } else {
          this.loadAuditContext();
          this.loadActionAudits(this.action.AuditLocation?.LocationID);
          this.loadChildActions(-1, true);
        }
        this.loadAssets(actionId);
        this.showAuditRequirementPanel = this.action.ActionVariationID === 2 && !!this.getRequiredAuditName();
        if (this.showAuditRequirementPanel && (!this.action.AuditResultID || this.action.AuditResultID <= 0)){
          this.loadEligibleAudits();
        }
        this.loadAssets(actionId);
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private loadHistory(actionId:number){
    this.actionService.getActionHistory(actionId).subscribe({
      next: (h:ActionHistory[]) => this.history = h ?? [],
      error: () => this.history = []
    });
  }

  private loadUsers(){
    const locationId = this.action.AuditLocation?.LocationID ?? -1;
    const req = new ActionUserRequest({ LocationID: locationId, EventID: this.action.EventID ?? -1 });
    this.actionService.getEventActionUsers(req).subscribe({
      next: (users:BasicUser[]) => {
        this.users = users ?? [];
        if (!this.users.some(u => u.UserID === -1)){
          this.users.unshift(new BasicUser({UserID:-1, DisplayName:'Please Select...', UserTypeID:0}));
        }
      },
      error: () => { this.users = []; }
    });
  }

  private loadEventContext(eventId:number){
    this.eventService.getEvent(eventId).subscribe({
      next: (res:GetEventResult) => {
        const ov: EventOverview | undefined = res?.EventOverview;
        if (ov){
          this.contextType = 'Event';
          this.contextTitle = ov.SummaryQuestion || 'Event';
          this.contextSubtitle = ov.LocationName || '';
          this.contextBody = ov.TypeQuestion || '';
          this.contextMeta = `${ov.DateQuestion} - Reported by ${ov.UserCreated}`;
        }
      },
      error: () => {}
    });
  }

  private loadAuditContext(){
    this.contextType = 'Audit';
    this.contextTitle = this.action?.AuditText || 'Audit';
    this.contextSubtitle = this.action?.AuditLocation?.LocationName || '';
    this.contextBody = this.action?.QuestionHeader || '';
    const answerParts: string[] = [];
    if (this.action?.Result){
      answerParts.push(`Answer: ${this.action.Result}`);
    }
    if (this.action?.Reference){
      answerParts.push(`Reference: ${this.action.Reference}`);
    }
    this.contextMeta = answerParts.join(' | ');
  }

  toggleAuditSelector(){
    if (this.action.AuditResultID && this.action.AuditResultID > 0){
      return;
    }
    this.showAuditSelector = !this.showAuditSelector;
    if (this.showAuditSelector){
      this.selectedAuditResultId = -1;
    }
    if (this.showAuditSelector && this.eligibleAudits.length === 0){
      this.loadEligibleAudits();
    }
  }

  private loadEligibleAudits(){
    if (!this.action?.ActionID){
      return;
    }
    this.actionService.getEligibleAuditsForAction(this.action.ActionID).subscribe({
      next: list => {
        const placeholder = { label: 'Please Select...', value: -1, disabled: true };
        const mapped = (list || []).map(a => ({
          label: a.AuditName || 'Audit',
          value: a.AuditResultID,
          meta: `${new Date(a.DateRecorded).toLocaleDateString()} - ${a.CompletedBy || 'Unknown'}`
        }));
        this.eligibleAudits = [placeholder, ...mapped];
        this.selectedAuditResultId = -1;
      },
      error: () => {
        this.eligibleAudits = [{ label: 'Please Select...', value: -1, disabled: true }];
        this.selectedAuditResultId = -1;
      }
    });
  }

  attachSelectedAudit(){
    if (this.selectedAuditResultId === null || this.selectedAuditResultId === undefined || this.selectedAuditResultId <= 0 || !this.action?.ActionID){
      return;
    }
    this.attachingAudit = true;
    this.actionService.attachAuditToAction(this.action.ActionID, this.selectedAuditResultId).subscribe({
      next: res => {
        this.attachingAudit = false;
        if (res?.IsError){
          return;
        }
        this.showAuditSelector = false;
        // reload action to reflect resolved state and audit info
        this.loadAction(this.action.ActionID);
      },
      error: () => {
        this.attachingAudit = false;
      }
    });
  }

  private loadChildActions(eventId:number, childrenOnly:boolean = false){
    const parentId = childrenOnly ? this.action.ActionID : -1;
    const actionTypeFilter = (this.action.EventID && this.action.EventID !== -1) ? 2 : 0; // 0 = no filter so audit children are included
    this.actionService.getEventActions(eventId, childrenOnly, parentId, actionTypeFilter).subscribe({
      next: (actions:BasicAction[]) => {
        const items = actions ?? [];
        this.childActions = items
          .map(a => {
            if (a?.ActionDeadline){
              a.ActionDeadline = new Date(a.ActionDeadline);
            }
            return a;
          })
          .sort((a,b) => {
            const aTime = a?.ActionDeadline ? new Date(a.ActionDeadline).getTime() : Number.MAX_SAFE_INTEGER;
            const bTime = b?.ActionDeadline ? new Date(b.ActionDeadline).getTime() : Number.MAX_SAFE_INTEGER;
            return aTime - bTime;
          });
      },
      error: () => {
        this.childActions = [];
      }
    });
  }

  private loadAssets(actionId: number){
    this.loadPhotos(actionId);
    this.loadAttachments(actionId);
  }

  confirmRemovePhoto(payload: { guid: string; event: Event }){
    if (!payload?.guid){ return; }
    const guid = this.extractGuidFromPhotoSrc(payload.guid);
    if (!guid){ return; }
    this.confirmationService.confirm({
      target: payload.event?.target as any,
      key: 'actionConfirm',
      header: 'Remove photo',
      message: 'Are you sure you want to remove this photo?',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      acceptButtonStyleClass: 'confirm-remove-btn',
      rejectButtonStyleClass: 'confirm-keep-btn',
      accept: () => this.removePhoto(guid),
      reject: () => this.confirmationService.close(),
      styleClass: 'confirm-popup-danger'
    } as any);
  }

  addUpdate(note:string){
    const trimmed = note?.trim();
    if (!trimmed || trimmed.length === 0){
      this.updateError = 'Update cannot be empty.';
      return;
    }
    this.updateError = '';
    this.updateSaving = true;
    const ar = new ActionResponse();
    ar.ActionID = this.action.ActionID;
    ar.Note = trimmed;
    ar.Resolved = this.action.Resolved;
    ar.ActionPhotos = this.action.ActionPhotos ?? [];
    ar.ActionAttachments = this.action.ActionAttachments ?? [];
    this.actionService.updateAction(ar).subscribe({
      next: (res: BasicResult) => {
        this.updateSaving = false;
        if (res?.IsError){
          this.updateError = 'Unable to add update. Please try again.';
          return;
        }
        this.updates = [{User:'You', Note:trimmed, Date:new Date()}, ...this.updates];
        this.updateError = '';
      },
      error: () => {
        this.updateSaving = false;
        this.updateError = 'Unable to add update. Please try again.';
      }
    });
  }

  addLocalUpdate(note:string){
    this.updates = [{User:'You', Note:note, Date:new Date()}, ...this.updates];
  }

  updateAssignee(user: BasicUser){
    if (user){
      this.action.ActionAssignee = user.DisplayName;
      this.action.ActionOwnerID = user.UserID;
      this.action.ActionOwnerTypeID = user.UserTypeID;
    }
  }

  updateDeadline(date: Date){
    this.action.ActionDeadline = date;
  }

  toggleResolved(){
    const original = this.action.Resolved;
    const originalResolvedDate = this.action.ResolvedDate;
    this.action.Resolved = !this.action.Resolved;
    this.action.ResolvedDate = this.action.Resolved ? new Date() : undefined as any;
    this.saving = true;
    this.actionService.modifyAction(this.action).subscribe({
      next: (res) => {
        this.saving = false;
        if (res?.IsError){
          this.validationError = 'Unable to update resolved state. Please try again.';
          this.action.Resolved = original;
          this.action.ResolvedDate = originalResolvedDate;
        }
      },
      error: () => {
        this.saving = false;
        this.validationError = 'Unable to update resolved state. Please try again.';
        this.action.Resolved = original;
        this.action.ResolvedDate = originalResolvedDate;
      }
    });
  }

  toggleSummaryEdit(){
    if (!this.editingSummary){
      this.editingSummary = true;
      this.validationError = '';
      this.descriptionError = '';
      this.assigneeError = '';
      this.dateError = '';
      this.summarySnapshot = this.getSummarySnapshot();
      return;
    }
    // attempting to save edits
    if (!this.hasSummaryChanges()){
      this.editingSummary = false;
      this.validationError = '';
      this.descriptionError = '';
      this.assigneeError = '';
      this.dateError = '';
      return;
    }
    if (!this.validateAction()){
      return;
    }
    this.saveActionChanges();
  }

  createChild(){
    const nextId = Math.max(...this.childActions.map(c=>c.ActionID), 200) + 1;
    this.childActions = [new BasicAction({ActionID: nextId, ActionDescription:'New child action', AssignedTo:'', ActionDeadline:new Date(), Resolved:false}), ...this.childActions];
  }

  openChild(id:number){
    if (id && id !== -1){
      this.route.queryParams.subscribe(params => {});
      window.location.href = `/actionv2?id=${id}`;
    }
  }

  downloadAttachment(guid:string){
    if (!guid){ return; }
    this.actionService.downloadActionFile(guid).subscribe({
      next: (res:any) => {
        const blob = res?.body ?? res;
        if (!blob){
          return;
        }
        const headers = res?.headers;
        const contentDisposition = headers?.get?.('content-disposition') || headers?.get?.('Content-Disposition') || '';
        let filename = this.attachments.find(a => a.GUID === guid)?.FileName || 'attachment';
        const starMatch = /filename\*\s*=\s*(?:UTF-8''|)([^;]+)/i.exec(contentDisposition);
        if (starMatch && starMatch[1]){
          const cleaned = starMatch[1].trim().replace(/(^['"]|['"]$)/g, '');
          filename = decodeURIComponent(cleaned);
        } else {
          const stdMatch = /filename\s*=\s*\"?([^\";]+)\"?/i.exec(contentDisposition);
          if (stdMatch && stdMatch[1]){
            filename = stdMatch[1];
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {}
    });
  }

  startChildAdd(){
    this.childAdding = true;
    this.childResetToken++;
  }

  cancelChildAdd(){
    this.childAdding = false;
    this.childResetToken++;
  }

  saveChildAction(child: ComplianceAction){
    this.childSaving = true;
    child.ParentActionID = this.action.ActionID;
    child.LocationID = this.action.AuditLocation?.LocationID ?? -1;
    // Event-scoped actions use the event endpoint; audit-scoped children use the generic child endpoint.
    if (this.action.EventID && this.action.EventID !== -1){
      child.EventID = this.action.EventID;
      child.ActionTypeID = 2;
      this.actionService.saveEventAction(child).subscribe({
        next: (res: BasicResult) => {
          this.childSaving = false;
          this.childAdding = false;
          if (!res?.IsError){
            this.loadChildActions(this.action.EventID, true);
          }
        },
        error: () => {
          this.childSaving = false;
          this.childAdding = false;
        }
      });
    } else {
      child.ActionTypeID = 1;
      this.actionService.saveNewAction(child).subscribe({
        next: (res: BasicResult) => {
          this.childSaving = false;
          this.childAdding = false;
          if (!res?.IsError){
            this.loadChildActions(-1, true);
          }
        },
        error: () => {
          this.childSaving = false;
          this.childAdding = false;
        }
      });
    }
  }

  private hydrateUpdates(){
    const responses = this.action?.ActionResponseList ?? [];
    this.updates = responses.map(r => ({
      User: r.Username || '',
      Note: r.Note,
      Date: r.ResponseDate ? new Date(r.ResponseDate) : new Date()
    })).sort((a,b) => (b.Date as any) - (a.Date as any));
  }

  private loadPhotos(actionId: number){
    this.actionService.getActionPhotos(actionId).subscribe({
      next: (list: string[]) => {
        const mapped = (list ?? []).map(p => this.photoSrc(p));
        this.photos = mapped;
        this.action.ActionPhotos = list ?? [];
        this.photoUploading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.photos = (this.action?.ActionPhotos ?? []).map(p => this.photoSrc(p));
        this.photoUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadActionAudits(locationId?: number){
    if (!locationId || locationId <= 0){
      this.actionAuditOptions = [];
      return;
    }
    this.loadingActionAudits = true;
    this.userManagement.getLocationAudits(locationId).subscribe({
      next: audits => {
        this.loadingActionAudits = false;
        this.actionAuditOptions = (audits || [])
          .filter(a => a?.AuditID && a.AuditID > 0)
          .map(a => ({ AuditID: a.AuditID, AuditName: a.AuditName || `Audit ${a.AuditID}` }))
          .sort((a,b) => a.AuditName.localeCompare(b.AuditName));
      },
      error: () => {
        this.loadingActionAudits = false;
        this.actionAuditOptions = [];
      }
    });
  }

  private loadAttachments(actionId: number){
    this.actionService.getActionFiles(actionId).subscribe({
      next: (res: any) => {
        const body = res?.body ?? res ?? this.action?.ActionAttachments ?? [];
        const mapped = (body || []).map((a:any) => {
          if (typeof a === 'string'){
            return {
              vm: { FileName: a, GUID: a } as ActionAttachmentVM,
              qa: new QuestionAttachment({ FileName: a, GUID: a, MimeType: '', Length: 0 })
            };
          }
          const guid = a?.GUID || a?.Guid || a?.guid || '';
          const fileName = a?.FileName || a?.fileName || 'Attachment';
          return {
            vm: { FileName: fileName, GUID: guid, MimeType: a?.MimeType || a?.mimeType || '' } as ActionAttachmentVM,
            qa: new QuestionAttachment({
              FileName: fileName,
              GUID: guid,
              MimeType: a?.MimeType || a?.mimeType || '',
              Length: a?.Length || a?.length || 0
            })
          };
        });
        this.attachments = mapped.map((m:any) => m.vm);
        this.action.ActionAttachments = mapped.map((m:any) => m.qa);
      },
      error: () => {
        const existing = this.action?.ActionAttachments ?? [];
        this.attachments = existing.map(a => ({
          FileName: a.FileName || 'Attachment',
          GUID: a.GUID,
          MimeType: a.MimeType || ''
        }));
        this.action.ActionAttachments = existing;
      }
    });
  }

  private photoSrc(guid: string): string{
    if (!guid){ return ''; }
    if (guid.startsWith('http')){ return guid; }
    return `${environment.apiUrl}actions/getphoto/${guid}`;
  }

  private validateAction(): boolean{
    this.validationError = '';
    this.descriptionError = '';
    this.assigneeError = '';
    this.dateError = '';
    let isValid = true;
    if (!this.action.ActionDescription || this.action.ActionDescription.trim().length === 0){
      this.descriptionError = 'Description is required.';
      isValid = false;
    }
    if (!this.action.ActionOwnerID || this.action.ActionOwnerID === -1){
      this.assigneeError = 'Please assign an owner.';
      isValid = false;
    }
    if (this.action.ActionDeadline){
      const today = new Date(); today.setHours(0,0,0,0);
      const due = new Date(this.action.ActionDeadline); due.setHours(0,0,0,0);
      if (due < today && (!this.originalDeadline || due.getTime() !== this.originalDeadline.getTime())){
        this.dateError = 'Due date cannot be in the past.';
        isValid = false;
      }
    }
    return isValid;
  }

  private saveActionChanges(){
    this.saving = true;
    this.validationError = '';
    this.actionService.modifyAction(this.action).subscribe({
      next: (res: BasicResult) => {
        this.saving = false;
        if (res?.IsError){
          this.validationError = 'Unable to save changes. Please try again.';
          return;
        }
        this.editingSummary = false;
        this.summarySnapshot = null;
      },
      error: () => {
        this.saving = false;
        this.validationError = 'Unable to save changes. Please try again.';
      }
    });
  }

  handlePhotoUpload(file: File){
    if (!file){ return; }
    if (!this.action?.ActionID || this.action.ActionID === -1){
      this.assetError = 'Cannot upload without an action.';
      return;
    }
    if (file.type && !file.type.startsWith('image/')){
      this.assetError = 'Please upload an image file.';
      return;
    }
    this.assetError = '';
    this.photoUploading = true;
    const reader = new FileReader();
    reader.onload = () => {
      const photoString = reader.result as string;
      const base64 = this.sanitizeBase64(photoString);
      const ap = new ActionPhoto({ ActionID: this.action.ActionID, Photo: base64 });
      this.actionService.uploadActionPhoto(ap).subscribe({
        next: (res: any) => {
          this.photoUploading = false;
          const body = res?.body ?? res;
          if (body?.IsError){
          this.assetError = 'Unable to upload photo. Please try again.';
          return;
        }
        this.loadPhotos(this.action.ActionID);
        this.loadHistory(this.action.ActionID);
      },
      error: () => {
        this.photoUploading = false;
        this.assetError = 'Unable to upload photo. Please try again.';
      }
    });
    };
    reader.onerror = () => {
      this.photoUploading = false;
      this.assetError = 'Unable to read photo file.';
    };
    reader.readAsDataURL(file);
  }

  handleAttachmentUpload(file: File){
    if (!file){ return; }
    if (!this.action?.ActionID || this.action.ActionID === -1){
      this.assetError = 'Cannot upload without an action.';
      return;
    }
    this.assetError = '';
    this.attachmentUploading = true;
    const af = new ActionFile({ ActionID: this.action.ActionID, FileData: file });
    this.actionService.uploadActionFile(af).subscribe({
      next: (event: any) => {
        if (event?.type === HttpEventType.Response){
          this.attachmentUploading = false;
          const res = event.body;
          if (res?.IsError){
          this.assetError = 'Unable to upload attachment. Please try again.';
          return;
        }
        this.loadAttachments(this.action.ActionID);
        this.loadHistory(this.action.ActionID);
      }
    },
    error: () => {
      this.attachmentUploading = false;
      this.assetError = 'Unable to upload attachment. Please try again.';
    }
  });
  }

  confirmRemoveAttachment(payload: { guid: string; event: Event }){
    if (!payload?.guid){ return; }
    const config: any = {
      target: payload.event.target as any,
      key: 'actionConfirm',
      header: 'Remove attachment',
      message: 'Are you sure you want to remove this attachment?',
      defaultFocus: 'reject',
      acceptLabel: 'Remove',
      rejectLabel: 'Keep',
      acceptIcon: 'pi pi-trash',
      rejectIcon: 'pi pi-undo',
      acceptButtonStyleClass: 'confirm-remove-btn',
      rejectButtonStyleClass: 'confirm-keep-btn',
      accept: () => this.removeAttachment(payload.guid)
    };
    // styleClass not typed in some PrimeNG versions; allow via any.
    config.styleClass = 'confirm-popup-danger';
    this.confirmationService.confirm(config);
  }

  onConfirmReject(confirmation: any){
    confirmation?.rejectEvent?.emit();
    this.confirmationService.close();
  }

  onConfirmAccept(confirmation: any){
    confirmation?.acceptEvent?.emit();
    this.confirmationService.close();
  }

  removeAttachment(guid: string){
    if (!guid || !this.action?.ActionID || this.action.ActionID === -1){
      return;
    }
    const current = this.action.ActionAttachments ?? [];
    const remaining = current.filter(a => a.GUID !== guid);
    if (remaining.length === current.length){
      return;
    }
    this.assetError = '';
    this.attachmentUploading = true;
    const response = new ActionResponse({
      ActionID: this.action.ActionID,
      Resolved: this.action.Resolved,
      Note: '',
      ActionPhotos: this.action.ActionPhotos ?? [],
      ActionAttachments: remaining
    });
    this.actionService.updateAction(response).subscribe({
      next: (res: BasicResult) => {
        this.attachmentUploading = false;
        if (res?.IsError){
          this.assetError = 'Unable to remove attachment. Please try again.';
          return;
        }
        this.action.ActionAttachments = remaining;
        this.attachments = this.attachments.filter(a => a.GUID !== guid);
        this.loadHistory(this.action.ActionID);
      },
      error: () => {
        this.attachmentUploading = false;
        this.assetError = 'Unable to remove attachment. Please try again.';
      }
    });
  }

  downloadPhoto(src: string){
    const guid = this.extractGuidFromPhotoSrc(src);
    if (!guid){ return; }
    this.actionService.downloadActionPhoto(guid).subscribe({
      next: (res:any) => {
        const blob = res?.body ?? res;
        if (!blob){
          return;
        }
        const headers = res?.headers;
        const contentDisposition = headers?.get?.('content-disposition') || headers?.get?.('Content-Disposition') || '';
        let filename = 'photo.jpg';
        const starMatch = /filename\\*\\s*=\\s*(?:UTF-8''|)([^;]+)/i.exec(contentDisposition);
        if (starMatch && starMatch[1]){
          const cleaned = starMatch[1].trim().replace(/(^['\"]|['\"]$)/g, '');
          filename = decodeURIComponent(cleaned);
        } else {
          const stdMatch = /filename\\s*=\\s*\"?([^\";]+)\"?/i.exec(contentDisposition);
          if (stdMatch && stdMatch[1]){
            filename = stdMatch[1];
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {}
    });
  }

  removePhoto(guid: string){
    if (!guid || !this.action?.ActionID || this.action.ActionID === -1){
      return;
    }
    const current = this.action.ActionPhotos ?? [];
    const remaining = current.filter(p => p !== guid);
    if (remaining.length === current.length){
      return;
    }
    this.assetError = '';
    this.photoUploading = true;
    const response = new ActionResponse({
      ActionID: this.action.ActionID,
      Resolved: this.action.Resolved,
      Note: '',
      ActionPhotos: remaining,
      ActionAttachments: this.action.ActionAttachments ?? []
    });
    this.actionService.updateAction(response).subscribe({
      next: (res: BasicResult) => {
        this.photoUploading = false;
        if (res?.IsError){
          this.assetError = 'Unable to remove photo. Please try again.';
          return;
        }
        this.action.ActionPhotos = remaining;
        this.photos = this.photos.filter(p => this.extractGuidFromPhotoSrc(p) !== guid);
        this.loadPhotos(this.action.ActionID);
        this.loadHistory(this.action.ActionID);
      },
      error: () => {
        this.photoUploading = false;
        this.assetError = 'Unable to remove photo. Please try again.';
      }
    });
  }

  private extractGuidFromPhotoSrc(src: string): string{
    if (!src){ return ''; }
    if (src.startsWith('data:')){ return ''; }
    const parts = src.split('/');
    const last = parts[parts.length - 1] || '';
    return last.split('?')[0];
  }

  handleCapturedPhoto(base64: string){
    if (!base64){ return; }
    if (!this.action?.ActionID || this.action.ActionID === -1){
      this.assetError = 'Cannot upload without an action.';
      return;
    }
    const preview = base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
    this.photos = [preview, ...(this.photos || [])];
    this.cdr.detectChanges();
    this.assetError = '';
    this.photoUploading = true;
    const cleaned = this.sanitizeBase64(base64);
    const ap = new ActionPhoto({ ActionID: this.action.ActionID, Photo: cleaned });
    this.actionService.uploadActionPhoto(ap).pipe(
      finalize(() => { this.photoUploading = false; })
    ).subscribe({
      next: (res: any) => {
        const body = res?.body ?? res;
        if (body?.IsError){
          this.assetError = 'Unable to upload photo. Please try again.';
          return;
        }
        this.loadPhotos(this.action.ActionID);
        this.loadHistory(this.action.ActionID);
      },
      error: () => {
        this.assetError = 'Unable to upload photo. Please try again.';
      }
    });
  }

  private sanitizeBase64(data: string): string{
    if (!data){ return ''; }
    const stripped = data.includes(',') ? data.split(',')[1] : data;
    const cleaned = stripped.replace(/[^A-Za-z0-9+/=]/g, '');
    const remainder = cleaned.length % 4;
    if (remainder === 0){
      return cleaned;
    }
    const paddingNeeded = 4 - remainder;
    return cleaned + '='.repeat(paddingNeeded);
  }

  getRequiredAuditName(): string {
    return this.action?.RequiredAuditName || this.action?.AuditName || '';
  }

  private getSummarySnapshot(){
    return {
      description: this.action?.ActionDescription || '',
      ownerId: this.action?.ActionOwnerID ?? -1,
      ownerTypeId: this.action?.ActionOwnerTypeID ?? -1,
      assignee: this.action?.ActionAssignee || '',
      deadline: this.action?.ActionDeadline ? new Date(this.action.ActionDeadline).getTime() : null
    };
  }

  private hasSummaryChanges(): boolean{
    const snapshot = this.summarySnapshot;
    if (!snapshot){
      return true;
    }
    const deadline = this.action?.ActionDeadline ? new Date(this.action.ActionDeadline).getTime() : null;
    return snapshot.description !== (this.action?.ActionDescription || '')
      || snapshot.ownerId !== (this.action?.ActionOwnerID ?? -1)
      || snapshot.ownerTypeId !== (this.action?.ActionOwnerTypeID ?? -1)
      || snapshot.assignee !== (this.action?.ActionAssignee || '')
      || snapshot.deadline !== deadline;
  }
}
